import { type FC, useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui';
import { useAuth } from '@/hooks';
import { supabase } from '@/lib/api/supabase';
import { OrderDetail } from '@/pages/components';
import type { Order, OrderItem } from '@/types';
import { dbToOrder, dbToOrderItem } from '@/types';

interface OrderWithItems extends Order {
  items: OrderItem[];
}

/**
 * Order detail page - fetches and displays a single order by ID
 */
export const OrderDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  const [order, setOrder] = useState<OrderWithItems | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { state: { returnTo: location.pathname } });
    }
  }, [authLoading, user, navigate]);

  // Fetch order with items
  useEffect(() => {
    const fetchOrder = async (): Promise<void> => {
      if (!user || !id) return;

      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('orders')
          .select(`
            *,
            items:order_items(*)
          `)
          .eq('id', id)
          .eq('user_id', user.id)
          .single();

        if (fetchError) throw fetchError;

        if (data) {
          const converted: OrderWithItems = {
            ...dbToOrder(data),
            items: (data.items || []).map((item: any) => dbToOrderItem(item)),
          };
          setOrder(converted);
        }
      } catch (err) {
        console.error('Failed to fetch order:', err);
        setError(err instanceof Error ? err.message : 'Failed to load order');
      } finally {
        setLoading(false);
      }
    };

    if (user && id) {
      void fetchOrder();
    }
  }, [user, id]);

  if (authLoading || loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-4 text-muted-foreground">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center text-red-600">
                <p className="font-medium">Error loading order</p>
                <p className="text-sm mt-2">{error || 'Order not found'}</p>
                <button
                  onClick={() => navigate('/orders')}
                  className="mt-4 bg-primary text-primary-foreground px-6 py-2 rounded-md hover:bg-primary/90"
                >
                  Back to Orders
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate('/orders')}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            &larr; Back to Orders
          </button>
        </div>
        <OrderDetail order={order} />
      </div>
    </div>
  );
};
